/**
 * 📊 Trends - Historiadatan ja trendien sivu
 *
 * Näyttää tietokannan historiadatan kuvaajina:
 * - Nilan ilmanvaihdon lämpötilat ja tilat
 * - Sähkön kokonaiskulutus (teho L1/L2/L3, kWh-mittarit)
 * - Sähkön kulutuserittely piireittäin
 *
 * Backend (TrendService) palauttaa "pitkän muodon" rivejä
 * ({ ts, register, avg_scaled_value, unit, description_fi }), jotka
 * ryhmitellään tässä rekisterikohtaisiksi sarjoiksi kuvaajaa varten.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel,
  MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useSocketService } from '../hooks/useSocket';
import { TrendChart, type ChartSeries } from '../components/TrendChart';

/** Validoitu kategorinen paletti (CVD-turvallinen, light/dark erikseen askellettu) */
const PALETTE_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const PALETTE_DARK  = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];

type QueryType = 'nilan_trend' | 'electricity_total_trend' | 'electricity_breakdown_trend';

const TREND_TYPES: { id: QueryType; label: string; description: string }[] = [
  { id: 'nilan_trend', label: '🌡️ Nilan ilmanvaihto', description: 'Lämpötilat ja toimintatilat' },
  { id: 'electricity_total_trend', label: '⚡ Sähkön kokonaiskulutus', description: 'Teho vaiheittain ja kWh-mittarit' },
  { id: 'electricity_breakdown_trend', label: '🔌 Kulutuserittely', description: 'Kulutus piireittäin' },
];

const RANGES = [
  { id: '24h', label: '24 h', hours: 24 },
  { id: '7d', label: '7 pv', hours: 24 * 7 },
  { id: '30d', label: '30 pv', hours: 24 * 30 },
  { id: '90d', label: '90 pv', hours: 24 * 90 },
  { id: '1y', label: '1 v', hours: 24 * 365 },
];

/** Backendin palauttama rivi (pitkä muoto) */
interface TrendRow {
  ts: string;
  register: number | string;
  avg_scaled_value: number | string | null;
  unit?: string | null;
  description_fi?: string | null;
  description_en?: string | null;
}

interface ParsedTrend {
  labels: string[];
  series: ChartSeries[];
  /** Yksikkö -> sarjat, jotta eri suureet piirtyvät omiin kuvaajiinsa (ei koskaan kahta y-akselia) */
  groups: { unit: string; series: ChartSeries[] }[];
}

/** MySQL DATETIME 'YYYY-MM-DD HH:MM:SS' (backend vertaa suoraan datetime-sarakkeeseen) */
function toSqlDateTime(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Pitkä muoto -> sarjat rekistereittäin, ryhmiteltynä yksikön mukaan */
function parseTrendRows(rows: TrendRow[], dark: boolean): ParsedTrend {
  const palette = dark ? PALETTE_DARK : PALETTE_LIGHT;

  // Aikaleimat esiintymisjärjestyksessä (SQL palauttaa jo aikajärjestyksessä)
  const labels: string[] = [];
  const labelIndex = new Map<string, number>();
  for (const r of rows) {
    if (!labelIndex.has(r.ts)) {
      labelIndex.set(r.ts, labels.length);
      labels.push(r.ts);
    }
  }

  // Rekisteri -> arvot
  const byRegister = new Map<string, { label: string; unit: string; values: (number | null)[] }>();
  for (const r of rows) {
    const key = String(r.register);
    if (!byRegister.has(key)) {
      byRegister.set(key, {
        label: r.description_fi || r.description_en || `Rekisteri ${key}`,
        unit: r.unit || '',
        values: new Array(labels.length).fill(null),
      });
    }
    const entry = byRegister.get(key)!;
    const idx = labelIndex.get(r.ts)!;
    const num = r.avg_scaled_value == null ? null : Number(r.avg_scaled_value);
    entry.values[idx] = num != null && isFinite(num) ? num : null;
  }

  // Sarjat, eniten dataa sisältävät ensin
  const all = Array.from(byRegister.entries())
    .map(([key, v]) => ({
      key,
      label: v.label,
      unit: v.unit,
      values: v.values,
      filled: v.values.filter((x) => x != null).length,
    }))
    .filter((s) => s.filled > 0)
    .sort((a, b) => b.filled - a.filled);

  // Väri seuraa sarjaa (kiinteä järjestys, ei koskaan kierrätetä)
  const series: ChartSeries[] = all.map((s, i) => ({
    key: s.key,
    label: s.label,
    unit: s.unit,
    color: palette[i % palette.length],
    values: s.values,
  }));

  // Ryhmittely yksikön mukaan: eri suureet omiin kuvaajiinsa
  const groupMap = new Map<string, ChartSeries[]>();
  for (const s of series) {
    const u = s.unit || '';
    if (!groupMap.has(u)) groupMap.set(u, []);
    groupMap.get(u)!.push(s);
  }
  const groups = Array.from(groupMap.entries())
    .map(([unit, s]) => ({ unit, series: s }))
    .sort((a, b) => b.series.length - a.series.length);

  return { labels, series, groups };
}

export const Trends: React.FC = () => {
  const { service, isConnected } = useSocketService();
  const [queryType, setQueryType] = useState<QueryType>('nilan_trend');
  const [rangeId, setRangeId] = useState('24h');
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [rows, setRows] = useState<TrendRow[]>([]);
  const [meta, setMeta] = useState<Record<string, unknown> | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const dark = typeof window !== 'undefined'
    && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;

  const load = useCallback(async () => {
    if (!isConnected) {
      setError('Ei yhteyttä palvelimeen');
      return;
    }
    const range = RANGES.find((r) => r.id === rangeId) ?? RANGES[0];
    const end = new Date();
    const start = new Date(end.getTime() - range.hours * 3600 * 1000);

    try {
      setLoading(true);
      setError(undefined);

      const response = await service.sendRequest({
        type: 'trend_query',
        data: {
          queryType,
          params: {
            startDate: toSqlDateTime(start),
            endDate: toSqlDateTime(end),
            maxRows: 800,
          },
        },
      });

      if (response.success && response.data) {
        const payload = response.data as { results?: TrendRow[]; metadata?: Record<string, unknown> };
        setRows(Array.isArray(payload.results) ? payload.results : []);
        setMeta(payload.metadata ?? null);
        setHidden(new Set());
      } else {
        setError(response.error || 'Trendin lataus epäonnistui');
        setRows([]);
        setMeta(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verkkovirhe');
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [isConnected, service, queryType, rangeId]);

  useEffect(() => { if (isConnected) load(); }, [isConnected, load]);

  const parsed = useMemo(() => parseTrendRows(rows, dark), [rows, dark]);
  const visible = useCallback((s: ChartSeries) => !hidden.has(s.key), [hidden]);

  const toggleSeries = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  if (!isConnected) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Yhdistetään palvelimeen…</Typography>
      </Stack>
    );
  }

  const activeType = TREND_TYPES.find((t) => t.id === queryType)!;
  const visibleSeries = parsed.series.filter(visible);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={700}>Trendit</Typography>
        <Typography color="text.secondary">Järjestelmän historiadata kuvaajina</Typography>
      </Box>

      {/* Suodattimet yhdellä rivillä kuvaajien yläpuolella */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="trend-type-label">Trendi</InputLabel>
            <Select
              labelId="trend-type-label"
              label="Trendi"
              value={queryType}
              onChange={(e) => setQueryType(e.target.value as QueryType)}
            >
              {TREND_TYPES.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={rangeId}
            onChange={(_, v) => v && setRangeId(v)}
            aria-label="Aikaväli"
          >
            {RANGES.map((r) => (
              <ToggleButton key={r.id} value={r.id}>{r.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
            aria-label="Näkymä"
          >
            <ToggleButton value="chart" aria-label="Kuvaaja"><ShowChartIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="table" aria-label="Taulukko"><TableChartIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Päivitä
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {activeType.description}
          {meta?.resultCount != null && ` · ${String(meta.resultCount)} riviä`}
          {meta?.intervalDays != null && ` · ${String(meta.intervalDays)} vrk`}
        </Typography>
      </Paper>

      {loading && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress size={28} />
          <Typography color="text.secondary" sx={{ mt: 2 }}>Ladataan trendiä…</Typography>
        </Paper>
      )}

      {error && !loading && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={load}>Yritä uudelleen</Button>}
        >
          {error}
        </Alert>
      )}

      {!loading && !error && parsed.series.length === 0 && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography fontWeight={600} gutterBottom>Ei dataa valitulla aikavälillä</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Kokeile pidempää aikaväliä (90 pv / 1 v) tai toista trendityyppiä.
            Jos pidempikään aikaväli ei näytä mitään, mittausdatan keruu on
            todennäköisesti keskeytynyt.
          </Typography>
          <Button variant="outlined" onClick={load}>Lataa uudelleen</Button>
        </Paper>
      )}

      {!loading && !error && parsed.series.length > 0 && (
        <>
          {/* Legenda: identiteetti ei koskaan pelkän värin varassa. Klikkaus piilottaa sarjan. */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Sarjat ({visibleSeries.length}/{parsed.series.length})
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {parsed.series.map((s) => (
                <Chip
                  key={s.key}
                  label={`${s.label}${s.unit ? ` (${s.unit})` : ''}`}
                  size="small"
                  onClick={() => toggleSeries(s.key)}
                  variant={visible(s) ? 'filled' : 'outlined'}
                  sx={{
                    borderLeft: `4px solid ${s.color}`,
                    opacity: visible(s) ? 1 : 0.5,
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Stack>
          </Paper>

          {view === 'chart' ? (
            // Eri yksiköt omiin kuvaajiinsa - ei koskaan kahta y-akselia samassa
            parsed.groups.map((g) => {
              const shown = g.series.filter(visible);
              if (!shown.length) return null;
              return (
                <Paper key={g.unit || 'no-unit'} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {g.unit ? `Yksikkö: ${g.unit}` : 'Mittaukset'}
                  </Typography>
                  <TrendChart labels={parsed.labels} series={shown} />
                </Paper>
              );
            })
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Aika</TableCell>
                    {visibleSeries.map((s) => (
                      <TableCell key={s.key} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {s.label}{s.unit ? ` (${s.unit})` : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.labels.map((label, i) => (
                    <TableRow key={label} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{label}</TableCell>
                      {visibleSeries.map((s) => (
                        <TableCell key={s.key} align="right">
                          {s.values[i] == null ? '–' : s.values[i]!.toFixed(1)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Stack>
  );
};

export default Trends;
